import { Button } from "components/atoms";
import Steps, { IStep } from "components/molecules/steps/Steps";

import LabelFormTemplate from "components/templates/label-form/LabelFormTemplate";
import { FormikProvider, useFormik } from "formik";
import i18next from "i18next";
import _ from "lodash";
import middleware from "middleware";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PiAddressBook,
  PiArrowLeft,
  PiArrowRight,
  PiBank,
  PiConfetti,
  PiHouseLine,
  PiPhone,
  PiWallet,
} from "react-icons/pi";
import { useSearchParams } from "react-router-dom";
import { useGetLabelByToken, useUpdateLabelByToken } from "services/labels";
import { CUSTOMER_TYPE, ILabelForm, LABEL_STATUS, SETTLEMENT_METHOD, TRUST_LEVEL } from "services/labels/types";
import { IAddress } from "services/types";
import { BUTTON_VARIANT } from "types/components/button/button.interface";
import { isUnder18 } from "utils/components/isUnder18";
import {
  conditionalField,
  getTabError,
  requiredEnumSelect,
} from "utils/global/validation.utils";
import { object, string } from "yup";
import LabelAddressStep from "./steps/LabelAddressStep";
import LabelBankStep from "./steps/LabelBankStep";
import LabelContactStep from "./steps/LabelContactStep";
import LabelFinalStep from "./steps/LabelFinalStep";
import LabelSupervisorStep from "./steps/LabelSupervisorStep";
import LabelTaxAddressStep from "./steps/LabelTaxAddressStep";

type IPublicLabelForm = Omit<ILabelForm, "settlementDeductionPercentage">;

const addressInitalValues: IAddress = {
  street: "",
  houseNumber: "",
  flatNumber: "",
  zipCode: "",
  city: "",
  countryAlfa2: "",
};

export const labelInitialValues: IPublicLabelForm = {
  labelStatus: LABEL_STATUS.Draft,
  customerType: CUSTOMER_TYPE.Company,
  labelName: "",
  email: "",
  phoneNumberPrefix: "",
  phoneNumber: "",
  firstName: "",
  lastName: "",
  dateOfBirth: new Date().toISOString().split("T")[0],
  address: addressInitalValues,
  taxOffice: {
    name: "",
    ...addressInitalValues,
  },
  legalSupervisorFirstName: "",
  legalSupervisorLastName: "",
  legalSupervisorCountryAlfa2: "",
  legalSupervisorPersonalId: "",
  bankAccountNumber: "",
  bankName: "",
  swift: "",
  personalIdCountryAlfa2: "",
  personalId: "",
  companyId: "",
  companyCountryAlfa2: "",
  notifyOnDocumentGeneration: false,
  defaultContracts: [],
  residenceCertificates: [],
  settlementMethod: SETTLEMENT_METHOD.unknown,
  routingNumber: null,
  notes: null,
  trustLevel: TRUST_LEVEL.Untrusted,
};

const addressValidationSchema = object().shape({
  street: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "$labelStatus",
  ),
  houseNumber: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "$labelStatus",
  ),
  zipCode: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "$labelStatus",
  ),
  city: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "$labelStatus",
  ),
  countryAlfa2: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "$labelStatus",
  ),
  flatNumber: string().nullable(),
});

const taxOfficeValidationSchema = addressValidationSchema.shape({
  name: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "$labelStatus",
  ),
});

const accountingTypeValidationSchema = (
  condition: (type: CUSTOMER_TYPE) => boolean,
) =>
  string().when("customerType", {
    is: condition,
    then: (schema) => schema.required(() => i18next.t("formik-error.required")),
    otherwise: (schema) => schema.nullable(),
  });

const physicalPersonMatureValidationSchema = string().when("customerType", {
  is: (type: CUSTOMER_TYPE) => type === CUSTOMER_TYPE.Physical,
  then: (schema) =>
    schema.when("dateOfBirth", {
      is: (date: string) => isUnder18(date),
      then: (schema) =>
        schema.required(() => i18next.t("formik-error.required")),
      otherwise: (schema) => schema.nullable(),
    }),
  otherwise: (schema) => schema.nullable(),
});

export const labelValidationSchema = object().shape({
  labelStatus: requiredEnumSelect(LABEL_STATUS),
  customerType: requiredEnumSelect(CUSTOMER_TYPE),
  labelName: string().required(() => i18next.t("formik-error.required")),
  email: conditionalField(
    string()
      .email(() => i18next.t("formik-error.invalid-email"))
      .required(() => i18next.t("formik-error.required")),
    "labelStatus",
    [-1, 1],
    string()
      .email(() => i18next.t("formik-error.invalid-email"))
      .optional()
      .nullable(),
  ),

  phoneNumberPrefix: string()
    .nullable()
    .test(
      "prefix-required-if-number",
      i18next.t("formik-error.required"),
      function (value) {
        const { phoneNumber } = this.parent;
        const hasPhoneNumber =
          typeof phoneNumber === "string" && phoneNumber.trim() !== "";
        const hasPrefix = typeof value === "string" && value.trim() !== "";

        // If phoneNumber is filled, phoneNumberPrefix must also be filled
        if (hasPhoneNumber && !hasPrefix) {
          return this.createError({
            path: this.path,
            message: i18next.t("formik-error.required"),
          });
        }

        return true;
      },
    ),

  phoneNumber: string()
    .nullable()
    .test(
      "number-required-if-prefix",
      i18next.t("formik-error.required"),
      function (value) {
        const { phoneNumberPrefix } = this.parent;
        const hasPrefix =
          typeof phoneNumberPrefix === "string" &&
          phoneNumberPrefix.trim() !== "";
        const hasPhoneNumber = typeof value === "string" && value.trim() !== "";

        if (hasPrefix && !hasPhoneNumber) {
          return this.createError({
            path: this.path,
            message: i18next.t("formik-error.required"),
          });
        }

        return true;
      },
    ),

  firstName: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "labelStatus",
  ),
  lastName: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "labelStatus",
  ),
  dateOfBirth: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "labelStatus",
  ),
  address: addressValidationSchema,
  taxOffice: taxOfficeValidationSchema,
  bankAccountNumber: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "labelStatus",
  ),
  bankName: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "labelStatus",
  ),
  personalIdCountryAlfa2: conditionalField(
    accountingTypeValidationSchema((value) => value === CUSTOMER_TYPE.Physical),
    "labelStatus",
  ),
  personalId: conditionalField(
    accountingTypeValidationSchema((value) => value === CUSTOMER_TYPE.Physical),
    "labelStatus",
  ),
  companyId: conditionalField(
    accountingTypeValidationSchema((value) => value === CUSTOMER_TYPE.Company),
    "labelStatus",
  ),
  companyCountryAlfa2: conditionalField(
    accountingTypeValidationSchema((value) => value === CUSTOMER_TYPE.Company),
    "labelStatus",
  ),
  legalSupervisorFirstName: conditionalField(
    physicalPersonMatureValidationSchema,
    "labelStatus",
  ),
  legalSupervisorLastName: conditionalField(
    physicalPersonMatureValidationSchema,
    "labelStatus",
  ),
  legalSupervisorCountryAlfa2: conditionalField(
    physicalPersonMatureValidationSchema,
    "labelStatus",
  ),
  legalSupervisorPersonalId: conditionalField(
    physicalPersonMatureValidationSchema,
    "labelStatus",
  ),
  swift: conditionalField(
    string().required(() => i18next.t("formik-error.required")),
    "labelStatus",
  ),
});

export type LabelFormSteps =
  | "contact"
  | "address"
  | "tax-office"
  | "supervisor"
  | "final"
  | "bank";

const LabelForm = () => {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const label = useGetLabelByToken(token, !!token);
  const updateLabel = useUpdateLabelByToken();

  const [isSuccess, setIsSuccess] = useState(false);

  const mapLabelToPublicForm = (labelData: ReturnType<typeof middleware.label.queryToForm>) => {
    const { settlementDeductionPercentage, ...publicLabelForm } = labelData;
    return publicLabelForm;
  };

  const initialValues = useMemo(
    () =>
      label.data
        ? mapLabelToPublicForm(middleware.label.queryToForm(label.data))
        : labelInitialValues,
    [label.data]
  );

  const formik = useFormik({
    initialValues,
    validationSchema: labelValidationSchema,
    enableReinitialize: true,
    initialStatus: {
      isLoading: label.isLoading,
      isPending: updateLabel.isPending,
    },
    onSubmit: async (values) => {
      try {
        await updateLabel.mutateAsync(
          {
            token,
            email: values.email,
            phoneNumberPrefix: values.phoneNumberPrefix,
            phoneNumber: values.phoneNumber,
            customerType: values.customerType,
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: values.dateOfBirth,
            address: values.address,
            taxOffice: values.taxOffice,
            legalSupervisorFirstName: values.legalSupervisorFirstName,
            legalSupervisorLastName: values.legalSupervisorLastName,
            legalSupervisorCountryAlfa2: values.legalSupervisorCountryAlfa2,
            legalSupervisorPersonalId: values.legalSupervisorPersonalId,
            bankAccountNumber: values.bankAccountNumber,
            bankName: values.bankName,
            swift: values.swift,
            personalIdCountryAlfa2: values.personalIdCountryAlfa2,
            personalId: values.personalId,
            companyId: values.companyId,
            companyCountryAlfa2: values.companyCountryAlfa2,
            routingNumber: values.routingNumber ?? null,
          },
          {
            onSuccess: () => {
              setIsSuccess(true);
            },
          },
        );
      } catch {
      }
    },
  });

  const [activeStep, setActiveStep] = useState<LabelFormSteps>("contact");

  const steps: IStep<LabelFormSteps>[] = [
    {
      icon: <PiPhone />,
      name: "contact",
      error: getTabError([
        formik.errors.customerType,
        formik.errors.firstName,
        formik.errors.lastName,
        formik.errors.email,
        formik.errors.phoneNumberPrefix,
        formik.errors.phoneNumber,
        formik.errors.dateOfBirth,
      ]),
    },
    ...(isUnder18(formik.values.dateOfBirth ?? "") &&
    formik.values.customerType === CUSTOMER_TYPE.Physical
      ? ([
          {
            icon: <PiAddressBook />,
            name: "supervisor",
            error: getTabError([
              formik.errors.legalSupervisorPersonalId,
              formik.errors.legalSupervisorFirstName,
              formik.errors.legalSupervisorLastName,
              formik.errors.legalSupervisorCountryAlfa2,
            ]),
          },
        ] as IStep<LabelFormSteps>[])
      : ([] as IStep<LabelFormSteps>[])),
    {
      icon: <PiHouseLine />,
      name: "address",
      error: getTabError([
        formik.errors.address?.city,
        formik.errors.address?.countryAlfa2,
        formik.errors.address?.houseNumber,
        formik.errors.address?.zipCode,
        formik.errors.address?.street,
        formik.errors.address?.flatNumber,
      ]),
    },
    {
      icon: <PiBank />,
      name: "tax-office",
      error: getTabError([
        formik.errors.taxOffice?.name,
        formik.errors.taxOffice?.city,
        formik.errors.taxOffice?.countryAlfa2,
        formik.errors.taxOffice?.houseNumber,
        formik.errors.taxOffice?.zipCode,
        formik.errors.taxOffice?.street,
        formik.errors.taxOffice?.flatNumber,
      ]),
    },
    {
      icon: <PiWallet />,
      name: "bank",
      error: getTabError([
        formik.errors.bankAccountNumber,
        formik.errors.bankName,
        formik.errors.personalIdCountryAlfa2,
        formik.errors.personalId,
        formik.errors.companyCountryAlfa2,
        formik.errors.companyId,
      ]),
    },
    {
      icon: <PiConfetti />,
      name: "final",
      error: !_.isEmpty(formik.errors) ? "Form error" : undefined,
    },
  ];

  const goToNextStep = () =>
    setActiveStep(
      steps[steps.findIndex((step) => step.name === activeStep) + 1].name,
    );
  const goToPrevStep = () =>
    setActiveStep(
      steps[steps.findIndex((step) => step.name === activeStep) - 1].name,
    );

  if (isSuccess) {
    return (
      <LabelFormTemplate
        header={t("label.form.success-header")}
        description={t("label.form.success-description")}
      >
        <div className="flex flex-col items-center justify-center py-8">
          <p className="text-lg text-center">{t("label.form.success-message")}</p>
        </div>
      </LabelFormTemplate>
    );
  }

  if (!token) {
    return (
      <LabelFormTemplate
        header={t("label.form.error-header")}
        description={t("label.form.error-description")}
      >
        <div className="flex flex-col items-center justify-center py-8">
          <p className="text-lg text-center text-red-500">{t("label.form.missing-token")}</p>
        </div>
      </LabelFormTemplate>
    );
  }

  if (label.isLoading) {
    return (
      <LabelFormTemplate
        header={t("label.form.initial-header")}
        description={t("label.form.initial-description")}
      >
        <div className="flex flex-col items-center justify-center py-8">
          <p className="text-lg text-center">{t("label.form.loading")}</p>
        </div>
      </LabelFormTemplate>
    );
  }

  if (!label.data) {
    return (
      <LabelFormTemplate
        header={t("label.form.error-header")}
        description={t("label.form.error-description")}
      >
        <div className="flex flex-col items-center justify-center py-8">
          <p className="text-lg text-center text-red-500">{t("label.form.invalid-token")}</p>
        </div>
      </LabelFormTemplate>
    );
  }

  return (
    <FormikProvider value={formik}>
      <LabelFormTemplate
        header={t("label.form.initial-header")}
        description={t("label.form.initial-description")}
        steps={
          <Steps<LabelFormSteps>
            steps={steps}
            activeStep={activeStep}
            onClickStep={(step) => setActiveStep(step)}
            i18nPrefix="label.form."
            showErrors={formik.submitCount > 0}
          />
        }
      >
        <form>
          {activeStep === "contact" && <LabelContactStep />}
          {activeStep === "supervisor" && <LabelSupervisorStep />}
          {activeStep === "address" && <LabelAddressStep />}
          {activeStep === "tax-office" && <LabelTaxAddressStep />}
          {activeStep === "bank" && <LabelBankStep />}
          {activeStep === "final" && <LabelFinalStep />}
          <div className="flex gap-2 w-full justify-between">
            {activeStep !== "contact" ? (
              <Button
                onlyIcon
                variant={BUTTON_VARIANT.default}
                onClick={goToPrevStep}
              >
                <Button.Description description={t("button.back")} />
                <Button.Icon icon={<PiArrowLeft />} />
              </Button>
            ) : (
              <div />
            )}
            {activeStep !== "final" && (
              <Button variant={BUTTON_VARIANT.submit} onClick={goToNextStep}>
                <Button.Text
                  text={t(
                    `label.form.${
                      steps[
                        steps.findIndex((step) => step.name === activeStep) + 1
                      ].name
                    }.name`,
                  )}
                />
                <Button.Icon icon={<PiArrowRight />} />
                <Button.Description description={t("button.next-step")} />
              </Button>
            )}
          </div>
        </form>
      </LabelFormTemplate>
    </FormikProvider>
  );
};

export default LabelForm;
